import React, { useState } from 'react';
import { Upload, Loader2, AlertCircle, Wand2, MessageSquare } from 'lucide-react';
import { editImage } from '../lib/api/edit/service';
import { useAuth } from '../contexts/AuthContext';
import { compressImage } from '../lib/utils/image';
import { saveTempUpload } from '../lib/firebase/db';

interface EditResponse {
  result: string | null;
  error?: string;
}

export function EditPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const handleEdit = async () => {
    if (!imageUrl || !prompt.trim() || !user) {
      setError('Please provide both an image and a prompt');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const editedImageUrl = await editImage(imageUrl, prompt.trim(), user.uid);
      setResult(editedImageUrl);
    } catch (err) {
      console.error('Edit failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to edit image');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    try {
      setUploadingImage(true);
      setError(null);

      // Compress the image before uploading
      const compressedFile = await compressImage(file, {
        maxWidth: 2048,
        maxHeight: 2048,
        quality: 0.8
      });

      // Upload to Firebase Storage with correct parameter order
      const uploadedUrl = await saveTempUpload(user.uid, compressedFile);
      setImageUrl(uploadedUrl);
    } catch (err) {
      console.error('Image upload failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-rmz-darker pt-16">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Image Upload */}
          <div className="bg-white dark:bg-rmz-gray rounded-xl p-6 border border-gray-200 dark:border-rmz-gray-light">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Upload Image</h2>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
              disabled={uploadingImage}
            />
            <label
              htmlFor="image-upload"
              className={`w-full aspect-square border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center cursor-pointer hover:border-rmz-primary transition-colors ${
                uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {uploadingImage ? (
                <div className="text-center">
                  <Loader2 className="w-8 h-8 mx-auto mb-2 text-gray-400 animate-spin" />
                  <span className="text-sm text-gray-500">Processing image...</span>
                </div>
              ) : imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="text-center">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <span className="text-sm text-gray-500">Click to upload or drag and drop</span>
                </div>
              )}
            </label>
          </div>

          {/* Prompt Input */}
          <div className="bg-white dark:bg-rmz-gray rounded-xl p-6 border border-gray-200 dark:border-rmz-gray-light">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-gray-500" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Prompt</h2>
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe how you want to edit the image..."
              className="w-full px-4 py-3 bg-gray-50 dark:bg-rmz-gray-light border border-gray-200 dark:border-rmz-gray-light rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rmz-primary/50 resize-none h-32"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Edit Button */}
          <button
            onClick={handleEdit}
            disabled={loading || uploadingImage || !imageUrl || !prompt.trim()}
            className="w-full px-6 py-3 bg-gradient-primary text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Editing...
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5" />
                Edit Image
              </>
            )}
          </button>

          {/* Result */}
          {result && (
            <div className="bg-white dark:bg-rmz-gray rounded-xl p-6 border border-gray-200 dark:border-rmz-gray-light">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Result</h2>
              <img
                src={result}
                alt="Edited"
                className="w-full rounded-lg"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}